import { Helmet } from 'react-helmet-async';
import { SITE_URL, LOGO_URL } from '../constants/calculatorPaths';

interface BaseCalculatorMetaProps {
  path: string;
  title: string;
  description: string;
  keywords: string;
  modifiedDate?: string;
  schemaType?: 'WebApplication' | 'WebPage';
  schemaFeatures?: string[];
  category?: string;
  breadcrumbs?: Array<{name: string, path: string}>;
}

export const BaseCalculatorMeta = ({
  path,
  title,
  description,
  keywords,
  modifiedDate = new Date().toISOString().split('T')[0],
  schemaType = 'WebApplication',
  schemaFeatures = [],
  category = '',
  breadcrumbs = []
}: BaseCalculatorMetaProps) => {
  const pageUrl = `${SITE_URL}/calculators/${path}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": schemaType,
    "name": title,
    "description": description,
    "url": pageUrl,
    "image": LOGO_URL,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Any",
    "dateModified": modifiedDate,
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": schemaFeatures,
    "publisher": {
      "@type": "Organization",
      "name": "Marketing Calculator Hub",
      "logo": {
        "@type": "ImageObject",
        "url": LOGO_URL
      }
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": SITE_URL
        },
        ...(category ? [{
          "@type": "ListItem",
          "position": 2,
          "name": category,
          "item": `${SITE_URL}/calculators#${category.toLowerCase().replace(/\s+/g, '-')}`
        }] : []),
        {
          "@type": "ListItem",
          "position": category ? 3 : 2,
          "name": title,
          "item": pageUrl
        }
      ]
    }
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={pageUrl} />
      
      {/* Last modified date */}
      <meta property="article:modified_time" content={modifiedDate} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={LOGO_URL} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={pageUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={LOGO_URL} />

      {/* Additional meta tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Marketing Calculator Hub" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}; 